import React, { useState } from "react";
import { useStore } from "@nanostores/react";
import { useCompleteTokenization } from "./completeTokenization";
import { useLookupAndTokenization } from "./lookupAndTokenization";
import { useLinkPayment } from "../useLinkPayment";
import { paymentCredentails, paymentInfo } from "../../store/nanostore";

export const useBankPaymentTokenization = (
  type: "stripe" | "tink" = "stripe",
  email?: string,
  phoneNumber?: string
) => {
  // Fetch data from useLinkPayment hook
  const { error, isLoading, loadAccount, isSuccess } = useLinkPayment(type);

  // Access payment information from nanostore
  const $paymentInfo = useStore(paymentInfo);

  // Initiate lookup and tokenization using email from paymentInfo
  const [request, getStatus] = useLookupAndTokenization(
    email || $paymentInfo?.payload?.email,
    phoneNumber || $paymentInfo?.payload?.phone,
    type
  ) as any;

  // Handle complete tokenization flow
  const { complete, request: completeRequest } = useCompleteTokenization();
  const [selectedAccount, setSelectedAccount] = useState<any>(null);

  // **Handle potential data change in request.data (lookup and tokenization response)**
  React.useEffect(
    () => {
      if (request?.data && !request.loading && request.called && !isSuccess) {
        // Check if createTokenization is a string (likely client secret for Stripe)
        if (
          typeof request.data?.createTokenization === "string" ||
          type === "tink"
        ) {
          // Update paymentCredentails nanostore with data from request
          paymentCredentails.set({
            ...request.data,
            stripeClientSecret: request.data?.createTokenization,
            type,
          });
          // Trigger loadAccount using the retrieved client secret
          loadAccount(request.data?.createTokenization);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [request.data]
  ); // Dependency on request.data

  // **Trigger complete tokenization on successful payment link creation**
  React.useEffect(() => {
    if (
      isSuccess &&
      !completeRequest.loading &&
      !completeRequest.called &&
      !isLoading
    ) {
      // Log for debugging purposes (optional)
      // If email exists in paymentInfo, initiate complete tokenization
      if ($paymentInfo?.payload?.email) {
        complete(
          email || $paymentInfo?.payload?.email,
          phoneNumber || $paymentInfo?.payload?.phone
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess]); // Dependency on isSuccess

  // **Trigger status update after complete tokenization succeeds**
  React.useEffect(() => {
    if (
      completeRequest.data &&
      !completeRequest.loading &&
      completeRequest.called &&
      !isLoading
    ) {
      getStatus(); // Re-fetch status after complete tokenization
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [completeRequest.data]); // Dependency on completeRequest.data

  // Extract bank information (tokens) from request.data or set an empty array as default
  const bankInfo = request?.data?.checkTokenizationStatus ||
    request?.data?.data?.checkTokenizationStatus || { tokens: [] };
  return {
    status: !error && !isLoading, // Combined status for success
    error,
    isLoading: isLoading || completeRequest.loading || request.loading,
    bankInfo,
    loadAccount,
    isSuccess,
    selectedAccount,
    setSelectedAccount,
    data: request?.data,
  };
};
